/**
 * React Query hooks for Administration > Access.
 *
 * Binary access-grant model (spec 00_access-binary-grants.md, 2026-08-09): a
 * grant is a set of accessSlug strings, no levels. Super-admin is a separate
 * flag toggled through its own endpoint. Contract confirmed against the api
 * rewrite 2026-08-09 (coordinator message): writes are per-slug and
 * incremental, not full-set replacement, so two admins editing the same
 * person at once cannot clobber each other's unrelated changes.
 *
 * - useAccessGrants()       GET /v1/access/grants
 * - useDirectorySearch(q)   GET /v1/access/directory?q= (enabled when q>=2)
 * - useGrantTool()          PUT /v1/access/grants/{principalRef}/{toolSlug}    { email?, name? } -- grants ONE tool
 * - useRevokeTool()         DELETE /v1/access/grants/{principalRef}/{toolSlug} -- revokes ONE tool
 * - useRevokeAllTools()     DELETE /v1/access/grants/{principalRef}            -- revokes every tool
 * - useGrantSuperAdmin()    PUT /v1/access/superadmin/{principalRef}           -- no body
 * - useRevokeSuperAdmin()   DELETE /v1/access/superadmin/{principalRef}        -- no body
 *
 * All endpoints require the caller to hold the admin.access accessSlug (or be
 * a super-admin). Mirrors the react-query patterns in useCoas.ts.
 *
 * GET /v1/access/tools also exists ({ tools: [{ slug, label, section }] }) but
 * is not used here: the UI derives the full grantable-tool list from the
 * catalog (accessScopes.ts), and this file does not depend on that endpoint
 * existing or being absent.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPut, apiPost, apiFetch } from './api';
import { API_BASE } from './apiBase';

export const ACCESS_API_BASE = `${API_BASE}/v1/access`;

export type AccessGrant = {
  principal_ref: string;
  email: string | null;
  name: string | null;
  super_admin: boolean;
  tools: string[]; // accessSlugs, e.g. ['commerce.orders', 'stores.sweetspot']
};

export type DirectoryUser = {
  id: string;
  displayName: string;
  mail: string | null;
};

type GrantsResponse = { grants: AccessGrant[] };
type DirectoryResponse = { users: DirectoryUser[] };

// Optional provisioning metadata carried on a grant/revoke call so the api can
// create the principal's grant record on first grant. Not required on every
// call (an existing grant record does not need re-provisioning), but cheap and
// safe to always send.
export type PrincipalMeta = {
  email?: string;
  name?: string;
};

function toolUrl(principalRef: string, toolSlug: string): string {
  return `${ACCESS_API_BASE}/grants/${encodeURIComponent(principalRef)}/${encodeURIComponent(toolSlug)}`;
}

export function useAccessGrants() {
  const query = useQuery<GrantsResponse, Error>({
    queryKey: ['access-grants'],
    queryFn: () => apiGet<GrantsResponse>(`${ACCESS_API_BASE}/grants`),
    staleTime: 15_000,
    retry: false,
  });
  return { grants: query.data?.grants ?? [], isLoading: query.isLoading, error: query.error };
}

export function useDirectorySearch(q: string) {
  const trimmed = q.trim();
  const enabled = trimmed.length >= 2;
  const query = useQuery<DirectoryResponse, Error>({
    queryKey: ['access-directory', trimmed],
    queryFn: () =>
      apiGet<DirectoryResponse>(`${ACCESS_API_BASE}/directory?q=${encodeURIComponent(trimmed)}`),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
  return {
    users: enabled ? query.data?.users ?? [] : [],
    isLoading: enabled && query.isLoading,
    error: query.error,
  };
}

// Grants ONE tool. PUT /v1/access/grants/{principalRef}/{toolSlug}.
export function useGrantTool() {
  const qc = useQueryClient();
  return useMutation<
    { ok: true },
    Error,
    { principalRef: string; toolSlug: string; meta?: PrincipalMeta }
  >({
    mutationFn: ({ principalRef, toolSlug, meta }) =>
      apiPut(toolUrl(principalRef, toolSlug), meta ?? {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['access-grants'] });
    },
  });
}

// Revokes ONE tool. DELETE /v1/access/grants/{principalRef}/{toolSlug}.
export function useRevokeTool() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { principalRef: string; toolSlug: string }>({
    mutationFn: ({ principalRef, toolSlug }) =>
      apiFetch(toolUrl(principalRef, toolSlug), { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['access-grants'] });
    },
  });
}

// Revokes EVERY tool for a person in one call. DELETE /v1/access/grants/{principalRef}.
export function useRevokeAllTools() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { principalRef: string }>({
    mutationFn: ({ principalRef }) =>
      apiFetch(`${ACCESS_API_BASE}/grants/${encodeURIComponent(principalRef)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['access-grants'] });
    },
  });
}

// Turns super-admin ON. PUT /v1/access/superadmin/{principalRef}, no body.
export function useGrantSuperAdmin() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { principalRef: string }>({
    mutationFn: ({ principalRef }) =>
      apiPut(`${ACCESS_API_BASE}/superadmin/${encodeURIComponent(principalRef)}`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['access-grants'] });
    },
  });
}

// Turns super-admin OFF. DELETE /v1/access/superadmin/{principalRef}, no body.
export function useRevokeSuperAdmin() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { principalRef: string }>({
    mutationFn: ({ principalRef }) =>
      apiFetch(`${ACCESS_API_BASE}/superadmin/${encodeURIComponent(principalRef)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['access-grants'] });
    },
  });
}

// Creates a new user in one call: provisions their per-tool grants and sends
// them an invitation email. POST /v1/access/users { email, name?, toolSlugs }.
// Unlike the per-slug PUT/DELETE used by the grant editor, this is the path for
// adding someone who does not exist yet, so it also fires the invite. Gated by
// the same admin.access authz as the grant writes.
export type CreateUserInput = { email: string; name?: string; toolSlugs: string[] };

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, CreateUserInput>({
    mutationFn: (body) => apiPost(`${ACCESS_API_BASE}/users`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['access-grants'] });
    },
  });
}

// admin.access is this tool's own accessSlug (shared/catalog.ts 'access' tool).
// Renamed from access.admin 2026-08-09 (binary access-grant model): every
// catalog tool's slug is namespaced section.tool, and the Access tool is no
// exception.
export function hasAccessAdmin(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms && perms.includes('admin.access');
}
